import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { MARKETS_BY_CODE, getStoredMarket, setStoredMarket } from "./markets";
import { getDriveShareRedirectUrl, getUsCrossBrandRedirectEnabled } from "@/lib/driveshareRedirect";
import { allowHistoryRewrite } from "@/lib/navigation-loop-guard";
import { SsrRequestContext } from "@/lib/ssr-bootstrap";
import i18n, { applyDisplayLanguage, type SupportedLanguage } from "./index";

/**
 * URL is the source of truth for (market, language) (task #865).
 *
 * Parsing rules — match the api-server's `pathToLocale()`:
 *   /uk            → market=uk, lang=undefined
 *   /uk/en/...     → market=uk, lang=en
 *   /de/en/cars/.. → market=de, lang=en
 *   /              → market=undefined (worldwide)
 *
 * The bare `/` worldwide page is allowed to render — we surface a soft
 * country-suggestion banner instead of auto-redirecting.
 */

/**
 * Universe of UI languages we know how to render. Used as a sanity guard
 * before consulting the per-market `languages` whitelist — prevents random
 * second segments from being mistaken for a language code.
 */
const SUPPORTED_LANG_CODES = new Set([
  "en", "fr", "de", "es", "it", "nl", "da", "sv", "no", "pt", "cs",
  "pl", "sk", "bg", "fi", "en-in", "pt-br",
]);

export interface LocaleState {
  /** Market segment from the URL ("uk", "de", …) or null when on bare `/`. */
  market: string | null;
  /** Language segment from the URL when explicit (`/de/en/...`), otherwise null. */
  language: string | null;
  /** Whether the URL explicitly carries a language segment. */
  hasUrlLanguage: boolean;
}

interface LocaleContextValue extends LocaleState {
  /** Path under the locale prefix, always starting with `/`. */
  innerPath: string;
  /** Build a full `BASE_URL + /<market>[/<lang>] + path` URL for `<a href>` etc. */
  localizedHref(path: string, options?: { market?: string | null; language?: string | null }): string;
  /** Navigate to a different market/language, preserving the inner path. */
  switchLocale(target: { market: string | null; language?: string | null; path?: string }): void;
}

function basePathPrefix(): string {
  const raw = (typeof import.meta !== "undefined" && import.meta.env?.BASE_URL) || "/";
  return raw.replace(/\/+$/, "");
}

export function parseLocaleFromPath(rawPath: string): LocaleState & { innerPath: string } {
  const base = basePathPrefix();
  let path = rawPath || "/";
  if (base && (path === base || path.startsWith(base + "/"))) {
    path = path.slice(base.length) || "/";
  }
  const segs = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (segs.length === 0) {
    return { market: null, language: null, hasUrlLanguage: false, innerPath: "/" };
  }
  const first = segs[0].toLowerCase();
  // `us` is INTENTIONALLY excluded — task #865 locks the contract that US
  // visitors live on driveshare.com, never on bookaclassic.com/us. The
  // LocaleProvider effect below cross-brand redirects /us/* requests so
  // they never linger as a valid in-app market path. `global` is similarly
  // a worldwide marker, not a routable URL segment.
  const market = first in MARKETS_BY_CODE && first !== "global" && first !== "us" ? first : null;
  if (!market) {
    return { market: null, language: null, hasUrlLanguage: false, innerPath: `/${segs.join("/")}` };
  }
  const second = segs[1]?.toLowerCase();
  const supportedForMarket = MARKETS_BY_CODE[market];
  // Accept the second segment as a language only if it's a known UI language
  // AND it's listed for this market in the SHARED matrix (markets.ts mirrors
  // the api-server's BAC_MARKETS map). This is how /ca/fr is recognised
  // (Canada: en, fr) but /uk/de is not (UK: en only).
  const marketLangs = new Set((supportedForMarket?.languages ?? [supportedForMarket?.defaultLanguage].filter(Boolean)).map((code) => code.toLowerCase()));
  // CSV-authored Brazilian paths can carry the generic source locale `pt`;
  // normalize that prefix to the supported Brazilian UI locale, pt-BR.
  const brazilianPortugueseAlias = market === "br" && second === "pt";
  const indianEnglishAlias = market === "in" && second === "en";
  const language = brazilianPortugueseAlias
    ? "pt-BR"
    : indianEnglishAlias
    ? "en-IN"
    : second && SUPPORTED_LANG_CODES.has(second) && marketLangs.has(second)
    ? supportedForMarket.languages.find((code) => code.toLowerCase() === second) ?? second
    : null;
  const offset = language ? 2 : 1;
  const inner = segs.length > offset ? `/${segs.slice(offset).join("/")}` : "/";
  return { market, language, hasUrlLanguage: !!language, innerPath: inner };
}

export function buildLocalePrefix(market?: string | null, language?: string | null): string {
  if (!market || market === "global" || market === "us") return "";
  const meta = MARKETS_BY_CODE[market];
  if (!meta) return "";
  const languageCode = language?.toLowerCase();
  const isMarketLanguage = languageCode && meta.languages.some((code) => code.toLowerCase() === languageCode);
  if (
    language &&
    languageCode &&
    languageCode !== meta.defaultLanguage.toLowerCase() &&
    isMarketLanguage &&
    SUPPORTED_LANG_CODES.has(languageCode)
  ) {
    return `/${market}/${language}`;
  }
  return `/${market}`;
}

/**
 * Path prefixes that MUST NOT carry a locale segment (task #865).
 *
 * Admin and dashboard surfaces are operator-facing tools — keeping them at a
 * single canonical URL avoids duplicate-content noise in analytics, keeps
 * impersonation/audit logs clean, and prevents wouter from trying to mount
 * `/uk/admin` style routes that don't exist in the route table. Both
 * `LocaleAwareRouterBase` (router base) and `switchLocale` (URL writer)
 * consult this list and skip prefixing.
 */
export const LOCALE_NEUTRAL_PREFIXES: readonly string[] = ["/admin", "/dashboard", "/apex-score"];

export function isLocaleNeutralPath(path: string): boolean {
  return LOCALE_NEUTRAL_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const ssrRequest = useContext(SsrRequestContext);
  const [state, setState] = useState<LocaleState & { innerPath: string }>(() =>
    ssrRequest
      ? parseLocaleFromPath(ssrRequest.pathname)
      : typeof window === "undefined"
      ? { market: null, language: null, hasUrlLanguage: false, innerPath: "/" }
      : parseLocaleFromPath(window.location.pathname),
  );

  // Country URLs determine the displayed language, but must not rewrite a
  // saved account preference. Bare worldwide pages retain the chosen/browser
  // language; URL language segments continue to use the existing SEO codes.
  useEffect(() => {
    if (!state.market) return;
    const requested = state.language || MARKETS_BY_CODE[state.market]?.defaultLanguage;
    if (!requested) return;
    const display = requested === "en"
      ? state.market === "uk" ? "en-GB" : "en-US"
      : requested;
    if (i18n.language !== display) void applyDisplayLanguage(display as SupportedLanguage);
  }, [state.market, state.language]);

  // Re-parse on every history change. Wouter monkey-patches
  // `history.pushState` / `history.replaceState` to dispatch matching named
  // events on `window` (see node_modules/wouter/src/use-browser-location.js)
  // and itself listens to `popstate` + `pushState` + `replaceState` +
  // `hashchange`. We MUST listen to the same set, otherwise an in-app
  // navigation that crosses a locale boundary (e.g. clicking a `~/uk/cars/…`
  // link from the worldwide `/` page or from `/trips/…`) updates wouter's
  // pathname via `pushState` while `LocaleProvider`'s `state.market` stays
  // stale. With a stale `market=null`, `LocaleAwareRouterBase` keeps `base=""`
  // even though the URL now carries `/uk/…`, so wouter's `<Switch>` sees the
  // raw `/uk/cars/:id/:slug` path against locale-agnostic routes (`/cars/:id/:slug`),
  // matches none of them, and falls through to `LandingPageCatchAll` → the
  // global 404 (task #1682). `bac:locale-change` is the synthetic event we
  // dispatch from `switchLocale` ourselves, kept here as a defensive
  // belt-and-braces in case the patched history events are ever stripped
  // (some test runners or extensions install their own history shims).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = () => setState(parseLocaleFromPath(window.location.pathname));
    const events: readonly string[] = [
      "popstate",
      "pushState",
      "replaceState",
      "hashchange",
      "bac:locale-change",
    ];
    for (const ev of events) window.addEventListener(ev, onChange);
    return () => {
      for (const ev of events) window.removeEventListener(ev, onChange);
    };
  }, []);

  // Persist URL-explicit market into local storage so a fresh tab on `/`
  // can restore the user's last choice (the soft-suggestion banner respects
  // this — see RegionSuggestionBanner).
  useEffect(() => {
    if (state.market && state.market !== getStoredMarket()) setStoredMarket(state.market);
  }, [state.market]);

  // Cross-brand redirect: /us/* → driveshare.com/* (task #865).
  // parseLocaleFromPath rejects "us" so the request would otherwise render
  // a 404 worldwide page; we explicitly forward to the sister brand instead.
  //
  // Gated on the `feature_us_cross_brand_redirect` flag (migration 049) so
  // dev/preview can opt out — without the flag, hitting /us/* in the
  // Replit preview was bouncing visitors to live driveshare.com.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const base = basePathPrefix();
    const stripped = base && window.location.pathname.startsWith(base)
      ? window.location.pathname.slice(base.length)
      : window.location.pathname;
    const segs = stripped.replace(/^\/+|\/+$/g, "").split("/");
    if (segs[0]?.toLowerCase() !== "us") return;
    // Drop the /us segment, preserve everything else (path, query, hash) and
    // hand off to driveshare.com so the visitor lands on the sister brand
    // page they were after — only when the flag is ON.
    const rest = segs.slice(1).join("/");
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    let cancelled = false;
    void (async () => {
      const enabled = await getUsCrossBrandRedirectEnabled();
      if (cancelled || !enabled) return;
      const origin = await getDriveShareRedirectUrl();
      if (cancelled) return;
      const target = `${origin.replace(/\/$/, "")}${rest ? `/${rest}` : ""}${search}${hash}`;
      window.location.replace(target);
    })();
    return () => { cancelled = true; };
  }, [state.innerPath]);

  // Strip the market prefix from locale-neutral URLs (/<market>/admin →
  // /admin, /<market>/dashboard/... → /dashboard/...). Operator surfaces
  // are single-canonical so the router and links stay locked to the bare
  // path; this also keeps server access logs clean. Done in a replaceState
  // because there's no semantic locale change — the visitor's preferred
  // market is already preserved in storage and resurfaces on the next
  // public page they visit.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!state.market) return;
    if (!isLocaleNeutralPath(state.innerPath)) return;
    const base = basePathPrefix();
    const next = `${base}${state.innerPath}` || "/";
    if (!allowHistoryRewrite(next)) return; // task #7376 loop guard
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    window.history.replaceState(null, "", `${next}${search}${hash}`);
    window.dispatchEvent(new Event("bac:locale-change"));
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [state.market, state.innerPath]);

  // Canonical-redirect /<market>/<defaultLang> → /<market> so we don't index
  // two URLs for the same content. e.g. /uk/en → /uk, /de/de → /de. Done in a
  // replaceState so the back button still works for the visitor.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!state.market || !state.hasUrlLanguage || !state.language) return;
    const meta = MARKETS_BY_CODE[state.market];
    if (!meta) return;
    if (state.language !== meta.defaultLanguage) return;
    const base = basePathPrefix();
    const trailing = state.innerPath === "/" ? "" : state.innerPath;
    const next = `${base}/${state.market}${trailing}` || "/";
    // Task #7376 — bail out instead of spinning if some other normaliser
    // keeps rewriting the URL back to the /<market>/<defaultLang> form
    // (Sentry DRIVESHARE_V2-E5: Safari replaceState rate-limit crash).
    if (!allowHistoryRewrite(next)) return;
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    window.history.replaceState(null, "", `${next}${search}${hash}`);
    window.dispatchEvent(new Event("bac:locale-change"));
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [state.market, state.language, state.hasUrlLanguage, state.innerPath]);

  const localizedHref = useCallback<LocaleContextValue["localizedHref"]>(
    (path, options) => {
      const market = options?.market !== undefined ? options.market : state.market;
      const language = options?.language !== undefined ? options.language : state.language;
      const base = basePathPrefix();
      const localePrefix = buildLocalePrefix(market, language);
      const trailing = path.startsWith("/") ? path : `/${path}`;
      const out = `${base}${localePrefix}${trailing === "/" ? "" : trailing}` || "/";
      return out || "/";
    },
    [state.market, state.language],
  );

  const switchLocale = useCallback<LocaleContextValue["switchLocale"]>(
    ({ market, language, path }) => {
      if (typeof window === "undefined") return;
      const innerPath = path !== undefined ? path : state.innerPath;
      const base = basePathPrefix();
      // Admin/dashboard URLs are locale-neutral by contract; never inject a
      // /<market> segment for them, even if the user picks a different
      // market while operating these tools.
      const localePrefix = isLocaleNeutralPath(innerPath)
        ? ""
        : buildLocalePrefix(market, language ?? null);
      const trailing = innerPath.startsWith("/") ? innerPath : `/${innerPath}`;
      const next = `${base}${localePrefix}${trailing === "/" ? "" : trailing}` || "/";
      const search = window.location.search || "";
      const hash = window.location.hash || "";
      window.history.pushState(null, "", `${next}${search}${hash}`);
      window.dispatchEvent(new Event("bac:locale-change"));
      // Wouter listens for `popstate` only; emit a synthetic one so client
      // routes pick up the new path without needing a full reload.
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
    [state.innerPath],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ ...state, localizedHref, switchLocale }),
    [state, localizedHref, switchLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Defensive fallback so consumers used in tests / storybook don't crash.
    const empty = { market: null, language: null, hasUrlLanguage: false, innerPath: "/" };
    return {
      ...empty,
      localizedHref: (p) => (p.startsWith("/") ? p : `/${p}`),
      switchLocale: () => { /* noop */ },
    };
  }
  return ctx;
}

/** Helper for framework-agnostic href building (e.g. inside `<Link>` props). */
export function localizedHrefFor(path: string, market?: string | null, language?: string | null): string {
  const base = basePathPrefix();
  const localePrefix = buildLocalePrefix(market, language);
  const trailing = path.startsWith("/") ? path : `/${path}`;
  return `${base}${localePrefix}${trailing === "/" ? "" : trailing}` || "/";
}
